import React, { useState, useEffect } from 'react';

interface CapitalProject {
  CapitalProjectId: string;
  ProjectId: string;
  Title: string;
  IsCapital: boolean;
  FinanceCostCenter?: string;
  FinanceBusinessUnit?: string;
  BudgetAmount?: number;
  FlaggedDate: string;
  FlaggedBy: string;
}

export default function CapitalProjectManager() {
  const [projects, setProjects] = useState<CapitalProject[]>([]);
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    projectId: '',
    costCenter: '',
    businessUnit: '',
    budgetAmount: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCapitalProjects();
    fetchAllProjects();
  }, []);

  const fetchCapitalProjects = async () => {
    try {
      const res = await fetch('/capital-projects');
      const data = await res.json();
      setProjects(data.data || []);
    } catch (err) {
      console.error('Failed to fetch capital projects:', err);
    }
  };

  const fetchAllProjects = async () => {
    try {
      const res = await fetch('/projects');
      const data = await res.json();
      setAllProjects(data.data || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/capital-projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: formData.projectId,
          costCenter: formData.costCenter || null,
          businessUnit: formData.businessUnit || null,
          budgetAmount: formData.budgetAmount ? parseFloat(formData.budgetAmount) : null,
          flaggedBy: 'admin@example.com',
        }),
      });

      const data = await res.json();
      if (data.success) {
        setFormData({ projectId: '', costCenter: '', businessUnit: '', budgetAmount: '' });
        setShowForm(false);
        fetchCapitalProjects();
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const availableProjects = allProjects.filter(
    p => !projects.find(cp => cp.ProjectId === p.ProjectId)
  );

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2>Capital Projects</h2>
      <button
        className="btn-primary"
        onClick={() => setShowForm(!showForm)}
        style={{ marginBottom: '1rem' }}
      >
        {showForm ? 'Cancel' : '+ Flag as Capital'}
      </button>

      {showForm && availableProjects.length > 0 && (
        <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          <div className="form-group">
            <label>Project</label>
            <select
              value={formData.projectId}
              onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
              required
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
            >
              <option value="">-- Select project --</option>
              {availableProjects.map(p => (
                <option key={p.ProjectId} value={p.ProjectId}>{p.Title}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Cost Center (Optional)</label>
            <input
              type="text"
              value={formData.costCenter}
              onChange={(e) => setFormData({ ...formData, costCenter: e.target.value })}
              placeholder="e.g., CC-2026-001"
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
            />
          </div>
          <div className="form-group">
            <label>Business Unit (Optional)</label>
            <input
              type="text"
              value={formData.businessUnit}
              onChange={(e) => setFormData({ ...formData, businessUnit: e.target.value })}
              placeholder="e.g., Product Development"
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
            />
          </div>
          <div className="form-group">
            <label>Budget Amount (USD, Optional)</label>
            <input
              type="number"
              value={formData.budgetAmount}
              onChange={(e) => setFormData({ ...formData, budgetAmount: e.target.value })}
              placeholder="e.g., 50000"
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Flagging...' : 'Flag Project'}
          </button>
        </form>
      )}

      <div>
        <h3>Flagged Projects ({projects.length})</h3>
        {projects.length === 0 ? (
          <p style={{ color: '#666' }}>No capital projects yet</p>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {projects.map(cp => (
              <div key={cp.CapitalProjectId} style={{ background: 'white', padding: '1rem', borderRadius: '4px', border: '2px solid #ffc107' }}>
                <h4 style={{ margin: 0 }}>{cp.Title}</h4>
                <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                  <strong>Cost Center:</strong> {cp.FinanceCostCenter || '-'}
                </p>
                <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                  <strong>Business Unit:</strong> {cp.FinanceBusinessUnit || '-'}
                </p>
                {cp.BudgetAmount && (
                  <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                    <strong>Budget:</strong> ${cp.BudgetAmount.toLocaleString()}
                  </p>
                )}
                <p style={{ margin: '0.5rem 0', color: '#999', fontSize: '0.85rem' }}>
                  Flagged by {cp.FlaggedBy} on {new Date(cp.FlaggedDate).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
